import dataUrl from './data.json';
import { useEffect, useState } from 'react';

interface ICountry {
  value: string;
  label: string;
}
interface IState {
  value: string;
  label: string;
}
interface ICity {
  value: string;
  label: string;
}

class GeoPicker {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private data: any;
  public initialized = false;

  constructor() {
    this.data = {};
  }

  public async init() {
    if (process.env.NODE_ENV === 'development' || !process.env.NODE_ENV) {
      // Directly import JSON data
      await this.loadLocalData();
    } else {
      // Fetch emitted JSON file with hashed name
      await this.loadData();
    }
    this.initialized = true;
  }

  countries(): ICountry[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Object.entries(this.data).map((c: any) => ({
      value: c?.[0],
      label: c?.[1]?.n,
    })) as ICountry[];
  }

  getCountryByCode(countryCode: string): ICountry | undefined {
    if (this.data?.[countryCode]) {
      return {
        value: countryCode,
        label: this.data?.[countryCode]?.n,
      };
    }
    return undefined;
  }

  getStateByCodeAndCountry(
    stateCode: string,
    countryCode: string
  ): IState | undefined {
    if (this.data?.[countryCode]) {
      if (this.data?.[countryCode]?.s?.[stateCode]) {
        return {
          value: stateCode,
          label: this.data?.[countryCode]?.s?.[stateCode]?.n,
        };
      }
    }
    return undefined;
  }

  getStates(countryCode: string): IState[] {
    if (this.data?.[countryCode]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return Object.entries(this.data?.[countryCode]?.s).map((c: any) => ({
        value: c[0],
        label: c?.[1]?.n,
      })) as IState[];
    }
    return [];
  }

  getCities(countryCode: string, stateCode: string): ICity[] {
    if (this.data?.[countryCode]) {
      if (this.data?.[countryCode]?.s?.[stateCode]) {
        const data = this.data?.[countryCode]?.s?.[stateCode]?.c as string[];
        return data.map((value) => ({
          value: value,
          label: value,
        }));
      }
    }
    return [];
  }

  private async loadLocalData() {
    try {
      const geoData = await import('./data.json');
      const data = geoData.default || geoData;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const response = await fetch(data);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      this.data = await response.json();
    } catch (error) {
      console.error('Error loading local geoData:', error);
    }
  }

  private async loadData() {
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const response = await fetch(dataUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      this.data = await response.json();
    } catch (error) {
      console.error('Error loading geoData:', error);
    }
  }
}

let geoPickerInstance: GeoPicker;

export const useGeoPicker = () => {
  const [functions, setFunctions] = useState({
    getCountries: () => [] as ICountry[],
    getCountryByCode: (countryCode: string) =>
      undefined as ICountry | undefined,
    getStateByCodeAndCountry: (countryCode: string, stateCode: string) =>
      undefined as IState | undefined,
    getStates: (countryCode: string) => [] as IState[],
    getCities: (countryCode: string, stateCode: string) => [] as ICity[],
  });

  useEffect(() => {
    (async () => {
      if (!geoPickerInstance) {
        geoPickerInstance = new GeoPicker();
      }
      if (!geoPickerInstance.initialized) {
        await geoPickerInstance.init();
      }
      setFunctions({
        getCountries: () => geoPickerInstance.countries(),
        getCountryByCode: (countryCode: string) =>
          geoPickerInstance.getCountryByCode(countryCode),
        getStateByCodeAndCountry: (countryCode: string, stateCode: string) =>
          geoPickerInstance.getStateByCodeAndCountry(countryCode, stateCode),
        getStates: (countryCode: string) =>
          geoPickerInstance.getStates(countryCode),
        getCities: (countryCode: string, stateCode: string) =>
          geoPickerInstance.getCities(countryCode, stateCode),
      });
    })();
  }, []);

  return functions;
};
